// cmp/admin/Users.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  FunnelIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from 'axios';
import { baseUrl } from '../../baseurl';


import Pagination from './Pagination'; // Adjust path as needed
axios.defaults.baseURL = baseUrl;

const Users = () => {
  const fileInputRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterRole, setFilterRole] = useState('all');
  const [refresh, setRefresh] = useState(false);
  
  const [openModal, setOpenModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  // Initialize with sample data
  useEffect(() => {
    loadUsers();
  }, [currentPage, itemsPerPage, searchTerm, filterRole, refresh]);

  useEffect(()=>{
  
  },[openModal]);

  const loadUsers = async () => {
    setLoading(true);
    const token = localStorage.getItem('relay_server_token');

    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add search term if provided
      if (searchTerm) {
        params.search = searchTerm;
      }

      // Add role filter if not 'all'
      if (filterRole && filterRole !== 'all') {
        params.role = filterRole;
      }

      const response = await axios.get('admin/get-user', {
        headers: {
          Authorization: `Bearer ${token}` 
        },
        params: params
      });

      if (response.data.success) {
        setUsers(response.data.users);
        setTotalUsers(response.data.totalUsers);
        setTotalPages(response.data.totalPages);
        setHasNextPage(response.data.hasNextPage);
        setHasPrevPage(response.data.hasPrevPage);
        setLoading(false);
      } else {
        toast.error("Failed to load users");
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error loading users");
      setLoading(false);
    }
  };

  // Pagination calculations for display
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(startIndex + users.length - 1, totalUsers);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedUsers([]); // Clear selections when page changes
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
    setSelectedUsers([]); // Clear selections
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilterRole(e.target.value);
  };

  // Debounced search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        setCurrentPage(1);
        setSelectedUsers([]);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const token = localStorage.getItem('relay_server_token');
        const response = await axios.delete('admin/delete-user/'+userId, {
          headers: {
            Authorization: `Bearer ${token}` 
          }
        });

        if (response.data.success) {
          toast.success("User deleted successfully");
          // Reload users to reflect changes
          loadUsers();
        } else {
          toast.error("Failed to delete user");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Error deleting user");
      }
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    const currentPageUserIds = users.map(user => user._id);
    setSelectedUsers(prev =>
      prev.length === currentPageUserIds.length && currentPageUserIds.length > 0
        ? []
        : currentPageUserIds
    );
  };

  const deleteSelectedUsers = async () => {
    if (selectedUsers.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
      try {
        const token = localStorage.getItem('relay_server_token');
        // You might need to implement a bulk delete endpoint
        // For now, delete one by one
        for (const userId of selectedUsers) {
          await axios.delete('admin/delete-user/'+userId, {
            headers: {
              Authorization: `Bearer ${token}` 
            }
          });
        }
        
        toast.success(`${selectedUsers.length} users deleted successfully`);
        setSelectedUsers([]);
        loadUsers(); // Reload users
      } catch (error) {
        toast.error("Error deleting selected users");
      }
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('relay_server_token');
      const response = await axios.put(
        'admin/update-user-status/'+userId,
        { isActive: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}` 
          }
        }
      );

      if (response.data.success) {
        toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        loadUsers(); // Reload to get updated data
      } else {
        toast.error("Failed to update user status");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating user status");
    }
  };

  const getStatusBadge = (isActive) => {
    const status = isActive ? 'active' : 'inactive';
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-800',
      teacher: 'bg-blue-100 text-blue-800',
      user: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[role] || 'bg-gray-100 text-gray-800'}`}>
        {role}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const resetPassword = (id) => {
    setSelectedUserId(id);
    setNewPassword("");
    setConfirmPassword("");
    setOpenModal(true);
  };

  // Handle API call to reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    let password = e.target.password.value;
    let c_password = e.target.confirmPassword.value;

    if (!password || !c_password) {
      toast.error("Both fields are required");
      return;
    }

    if (password !== c_password) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      const token = localStorage.getItem("relay_server_token");
      const response = await axios.post('admin/user-reset-password',
        { password, selectedUserId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Password reset successfully");
        setOpenModal(false);
      } else {
        toast.error("Failed to reset password");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error resetting password");
    }
  };

  const PasswordDesign = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Reset Password</h3>
                <p className="text-sm text-gray-500 mt-1">Create a new secure password</p>
              </div>
            </div>
            <button
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              onClick={() => setOpenModal(false)}
            >
              ✕
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleResetPassword}>
            <div className="p-6 space-y-6">
              {/* New Password */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">New Password</label>
                <input
                  type="password"
                  name="password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter new password"
                />
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex space-x-3 p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={() => setOpenModal(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Reset Password
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

 const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection and upload
  const handleFileUpload = async (event) => {
    const token = localStorage.getItem("relay_server_token");
    const file = event.target.files[0];
    if (!file) return;

    // ✅ Validate file type
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!validTypes.includes(file.type)) {
      alert("Please select a valid Excel file (.xlsx)");
      return;
    }

    setImportLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post('admin/user-import',
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "ClassRoom imported successfully!");
        setRefresh(true);
      } else {
        toast.error(response.data.message || "Import failed!");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Er");
    } finally {
      setImportLoading(false);
      event.target.value = "";
    }
  };

  const exportToCSV = async () => {
    try {
      const token = localStorage.getItem("relay_server_token");
      const response = await axios.get("admin/export-users/classRoom", {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      if (response.data.size === 0) {
        throw new Error('Empty file received');
      }

      if (response.data.type === 'application/json') {
        const errorText = await response.data.text();
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Export failed');
      }

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `users_${new Date().toISOString().split('T')[0]}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          alert(`Export failed: ${errorData.error || errorData.message || 'Unknown error'}`);
        } catch {
          alert('Export failed: Server error occurred');
        }
      } else {
        alert(`Export failed: ${error.message || 'Please try again'}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      { openModal &&  <PasswordDesign /> }
      
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Class Room Management</h1>
          <p className="text-gray-600">
            Total users: {totalUsers}
            {(searchTerm || filterRole !== 'all') && ` (${users.length} on this page)`}
          </p>
        </div>
        

        <div className='flex gap-2'>
          <Link
            to="add-user"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Class Room
          </Link>

          <button
            onClick={handleImportClick}
            disabled={importLoading}
            className="flex items-center px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
            {importLoading ? 'Importing...' : 'Import'}
          </button>
          
          <a
            href={`${baseUrl}admin/download-user-sample`}
            download
            className="flex items-center px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
            Sample
          </a>
          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
            Export
          </button>
        </div>
      </div>

      <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept=".csv,.xlsx,.xls"
          className="hidden"
        />

      {/* Toolbar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search users by name, email, or center..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={filterRole}
                onChange={handleFilterChange}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Roles</option>
                <option value="teacher">Teacher</option>
                <option value="user">User</option>
              </select>
            </div>

            {/* Bulk Actions */}
            {selectedUsers.length > 0 && (
              <button
                onClick={deleteSelectedUsers}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                Delete Selected ({selectedUsers.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-6 py-3">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  License
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Id
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Center
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                    {loading ? 'Loading users...' : 'No users found matching your criteria'}
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => toggleUserSelection(user._id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white capitalize font-bold">
                          {user.name?.charAt(0) || 'U'}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 capitalize">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(user.isActive)}
                        <button
                          onClick={() => toggleUserStatus(user._id, user.isActive)}
                          className={`p-1 rounded ${
                            user.isActive 
                              ? 'text-green-600 hover:bg-green-100' 
                              : 'text-red-600 hover:bg-red-100'
                          }`}
                        >
                          {user.isActive ? <CheckIcon className="w-4 h-4" /> : <XMarkIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.ip || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.license || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.user_id || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.center}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={"edit-user/"+user._id}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Link>

                        <button
                          onClick={()=>resetPassword(user._id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Reset password"
                        >
                          <ArrowPathIcon className="w-4 h-4" />

                        </button>
                        <button
                          onClick={() => deleteUser(user._id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalUsers}
            showingStart={totalUsers === 0 ? 0 : startIndex}
            showingEnd={endIndex}
          />
        )}
      </div>
    </div>
  );
};

export default Users;